import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, RotateCcw, Trash2, Users } from 'lucide-react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { InlineList, InlineListRow } from '@/components/InlineList';
import { Modal } from '@/components/Modal';
import type { AdminUsersResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import './AdminClassUsers.css';
import '../styles/common.css';

const parseEmails = (value: string) => value
  .split(/[,;\r\n]+/)
  .map(email => email.trim().toLowerCase())
  .filter(Boolean);

const AdminClassUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUsersResponse[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [emailsText, setEmailsText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [deletingUser, setDeletingUser] = useState<AdminUsersResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [reactivatingUserId, setReactivatingUserId] = useState<string | null>(null);
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classId = searchParams.get('classId');
  const className = searchParams.get('className') ?? 'Class';
  const from = searchParams.get('from') || 'modules';

  const fetchUsers = async () => {
    if (!classId) return;
    try {
      setLoading(true);
      setError(null);
      setUsers(await adminService.getUsersByClass(classId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Users could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void authService.getCurrentUser().then(setCurrentUser);
    if (!classId) {
      setError('No class selected');
      setLoading(false);
      return;
    }
    void fetchUsers();
  }, [classId]);

  const handleViewConversations = (user: AdminUsersResponse) => {
    const query = new URLSearchParams({ userId: user.id });
    if (classId) query.set('classId', classId);
    sessionStorage.setItem('adminUserConversationsFrom', from);
    navigate(`/admin/user-conversations?${query}`);
  };

  const openImport = () => {
    setEmailsText('');
    setImportError(null);
    setIsImportOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!classId || !deletingUser) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await adminService.deactivateUserInClass(classId, deletingUser.id);
      setDeletingUser(null);
      await fetchUsers();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'User could not be removed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReactivateUser = async (user: AdminUsersResponse) => {
    if (!classId) return;

    try {
      setReactivatingUserId(user.id);
      setReactivateError(null);
      await adminService.reactivateUserInClass(classId, user.id);
      await fetchUsers();
    } catch (err) {
      setReactivateError(err instanceof Error ? err.message : 'User could not be restored');
    } finally {
      setReactivatingUserId(null);
    }
  };

  const handleResendInvitation = async (event: React.MouseEvent, user: AdminUsersResponse) => {
    event.stopPropagation();
    if (!classId) return;

    try {
      setResendingUserId(user.id);
      setResendError(null);
      await adminService.resendInvitation(classId, user.email);
      await fetchUsers();
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Invitation could not be resent');
    } finally {
      setResendingUserId(null);
    }
  };

  const handleImport = async () => {
    if (!classId) return;
    const emails = parseEmails(emailsText);
    if (emails.length === 0) {
      setImportError('Please enter at least one email address.');
      return;
    }

    try {
      setIsImporting(true);
      setImportError(null);
      await adminService.importUsersToClass(classId, emails);
      setIsImportOpen(false);
      await fetchUsers();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Users could not be imported');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="admin-class-users-container">
      <AppHeader user={currentUser} title="KI Expert - Admin" subtitle={`Users for ${className}`} />
      <main className="admin-class-users-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              from === 'classes' ? { label: 'Classes', path: '/admin/classes' } : { label: 'Modules', path: '/admin/modules' },
              { label: `Users of class ${className}` },
            ]} />
            <h2 className="navigation-bar-title">Users of class {className}</h2>
          </div>
          <div className="navigation-bar-actions">
            <button className="btn btn-success" onClick={openImport} type="button">Add Users</button>
          </div>
        </div>

        {reactivateError && <p className="modal-error">{reactivateError}</p>}
        {resendError && <p className="modal-error">{resendError}</p>}
        {loading ? <div className="loading-container"><div className="loading-spinner" /><p>Loading users...</p></div>
          : error ? <div className="error-container"><h3>Error loading</h3><p>{error}</p><button className="btn btn-primary" onClick={() => void fetchUsers()} type="button">Try again</button></div>
          : users.length === 0 ? <div className="empty-container"><Users aria-hidden="true" size={40} /><h3>No users found</h3><p>Add users by importing email addresses to invite them to this class.</p></div>
          : <InlineList compact>{users.map(user => {
            const isPending = user.status === 'pending';
            const isActive = user.status === 'active';
            return (
              <InlineListRow
                actions={isPending
                  ? <button aria-label={`Resend invitation to ${user.email}`} className="class-action class-action-primary" data-tooltip="Resend invitation" disabled={resendingUserId === user.id} onClick={(event) => void handleResendInvitation(event, user)} type="button"><Mail aria-hidden="true" size={18} /></button>
                  : isActive
                    ? <button aria-label={`Remove ${user.firstName || user.email} from class`} className="class-action class-action-danger" data-tooltip="Remove" onClick={() => setDeletingUser(user)} type="button"><Trash2 aria-hidden="true" size={18} /></button>
                    : <button aria-label={`Restore ${user.firstName || user.email}`} className="class-action class-action-primary" data-tooltip="Restore" disabled={reactivatingUserId === user.id} onClick={() => void handleReactivateUser(user)} type="button"><RotateCcw aria-hidden="true" size={18} /></button>}
                className={!isActive && !isPending ? 'class-user-inactive' : ''}
                key={user.id}
                onClick={isPending ? undefined : () => handleViewConversations(user)}
              >
                <div className="class-user-details">
                  <h3>{user.firstName || user.email} {user.lastName}</h3>
                  <span>{user.email}{isPending && <span className="class-user-status-badge">Pending</span>}</span>
                </div>
              </InlineListRow>
            );
          })}</InlineList>}
      </main>

      {isImportOpen && <Modal
        footer={<><button className="btn btn-secondary" disabled={isImporting} onClick={() => setIsImportOpen(false)} type="button">Cancel</button><button className="btn btn-primary" disabled={isImporting} onClick={() => void handleImport()} type="button">{isImporting ? 'Adding...' : 'Add Users'}</button></>}
        onClose={() => !isImporting && setIsImportOpen(false)}
        title="Add users"
      >
        {importError && <p className="modal-error">{importError}</p>}
        <div className="modal-field">
          <label htmlFor="class-user-emails">Email addresses</label>
          <textarea id="class-user-emails" onChange={event => setEmailsText(event.target.value)} placeholder={'user1.email@domain.com;user2.email@domain.com\nseparation with , ; or line break'} rows={8} value={emailsText} />
        </div>
      </Modal>}

      {deletingUser && <Modal
        footer={<><button className="btn btn-secondary" disabled={isDeleting} onClick={() => setDeletingUser(null)} type="button">Cancel</button><button className="btn btn-danger" disabled={isDeleting} onClick={() => void handleDeleteUser()} type="button">{isDeleting ? 'Removing...' : 'Remove'}</button></>}
        onClose={() => !isDeleting && setDeletingUser(null)}
        title="Remove user from class"
      >
        {deleteError && <p className="modal-error">{deleteError}</p>}
        <p className="modal-confirmation-message">
          Should {deletingUser.firstName} {deletingUser.lastName} really be removed from class {className}? The conversation history will be preserved.
        </p>
      </Modal>}
    </div>
  );
};

export default AdminClassUsers;
